/**
 * RS-22 follow-up: province codes (BGD, KRB, BAS…) are an internal API
 * concept that mean nothing to a non-technical merchant. The UI must
 * always show the real province name (English title-cased OR Arabic) but
 * still submit the canonical province_code so it matches order data on
 * the backend.
 */

// Loose shape — accepts both RegionItem (where some fields are non-null)
// and the raw API row (where province_en_name can be null in faker rows).
interface ProvinceRow {
  province_code?: string | null;
  province_en_name?: string | null;
  province?: string | null; // Arabic
}

export interface ProvinceOption {
  value: string; // province_code (what we submit)
  label: string; // localized display name
}

// A province CODE is a short uppercase identifier — BGD, KRB, BSR. Measured
// against the live local feed: 18 rows match this and they are exactly the 18
// Iraqi governorates (AMA ANB ARB BAS BBL BGD DOH DWN DYL KOT KRB KRK MOS NAS
// NJF SAH SAM SMH). The other 5,121 rows carry a faker CITY NAME in the code
// column — "West Salvadorstad", "Port Winfieldborough".
const PROVINCE_CODE = /^[A-Z]{2,4}$/;

const isLikelyRealProvince = (row: ProvinceRow): boolean => {
  if (!row.province_code) return false;

  // This used to test only "has a code AND some name", on the belief that
  // faker rows have a null province_en_name. They do not — every faker row
  // carries a full set of invented names:
  //     {code: "West Salvadorstad", en: "West Emerald", ar: "Olsonland"}
  // so the old test passed all 5,139 rows through. That put 5,139 items into
  // a Radix Select, which does not virtualise, and rendering them froze the
  // main thread for ~2s on every screen that opens a province picker.
  //
  // Filtering on the SHAPE OF THE CODE is the right test because it checks the
  // field for what it is contractually — an identifier, not a place name — so
  // it stays correct even if the seed data changes its naming.
  if (!PROVINCE_CODE.test(row.province_code)) return false;

  return Boolean(row.province_en_name || row.province);
};

const titleCase = (s: string): string =>
  s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

const localizedName = (row: ProvinceRow, locale: 'en' | 'ar'): string => {
  if (locale === 'ar') {
    return (row.province ?? '').trim() || titleCase(row.province_en_name ?? '');
  }
  return titleCase((row.province_en_name ?? '').trim());
};

/**
 * Build a deduped, name-sorted list of province options for a select.
 * Filters out faker/garbage rows.
 */
export function buildProvinceOptions(
  rows: ProvinceRow[],
  locale: 'en' | 'ar' = 'en',
  // Codes the caller is ALREADY using — a saved rule, the value on the record
  // being edited. These are admitted whatever their shape.
  //
  // This exists because the filter above is a heuristic about a data set this
  // code cannot see all of. If production ever holds a province code that does
  // not look like the eighteen we measured, the heuristic would hide it — and
  // hiding a province a merchant has ALREADY SELECTED is the one failure that
  // silently changes their configuration. Admitting it costs one option.
  keepCodes: readonly string[] = [],
): ProvinceOption[] {
  const forced = new Set(keepCodes.filter(Boolean));

  const collect = (accept: (row: ProvinceRow) => boolean): ProvinceOption[] => {
    const seen = new Map<string, ProvinceOption>();
    for (const r of rows) {
      if (!accept(r)) continue;
      const code = r.province_code as string;
      if (seen.has(code)) continue;
      const label = localizedName(r, locale);
      if (!label) continue;
      seen.set(code, { value: code, label });
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const filtered = collect((r) => isLikelyRealProvince(r) || forced.has(r.province_code ?? ''));
  if (filtered.length > 0) return filtered;

  // FAIL OPEN. The filter is a heuristic; the feed is not fully knowable from
  // here. If it ever matches nothing while the feed clearly holds provinces,
  // the heuristic — not the data — is what is wrong, and an empty province
  // picker blocks the merchant completely. A slow, noisy list is recoverable;
  // a screen with no provinces at all is not.
  return collect((r) => Boolean(r.province_code) && Boolean(r.province_en_name || r.province));
}

/**
 * Look up a province display name from a code. Returns the original code
 * unchanged when the province is unknown so the UI never shows blank.
 */
export function resolveProvinceName(
  code: string | undefined,
  rows: ProvinceRow[] = [],
  locale: 'en' | 'ar' = 'en',
): string {
  if (!code) return '';
  for (const r of rows) {
    if (r.province_code === code) {
      const name = localizedName(r, locale);
      return name || code;
    }
  }
  return code;
}
